import axios from 'axios';

export const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance
const apiClient = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout
});

// Add auth token to requests
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem('auth_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Only redirect to login if user isn't an AALIGN auto-login user
      const user = localStorage.getItem('user');
      if (user) {
        try {
          const userData = JSON.parse(user);
          // AALIGN users have id instead of email as primary identifier
          if (userData.email && !userData.email.endsWith('@aalign.local')) {
            localStorage.removeItem('auth_token');
            localStorage.removeItem('user');
            localStorage.removeItem('connected_users');
            window.location.href = '/login';
          }
        } catch (e) {
          // Invalid user data, redirect to login
          localStorage.removeItem('auth_token');
          localStorage.removeItem('user');
          localStorage.removeItem('connected_users');
          window.location.href = '/login';
        }
      } else {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => apiClient.post('/auth/register', data),
  login: (data) => apiClient.post('/auth/login', data),
  getMe: () => apiClient.get('/auth/me'),
};

// Companies API
export const companiesAPI = {
  create: (data) => apiClient.post('/companies/', data),
  getMy: () => apiClient.get('/companies/my'),
  updateMy: (data) => apiClient.put('/companies/my', data),
};

// Listings API
export const listingsAPI = {
  getAll: (params) => apiClient.get('/listings/', { params }),
  getById: (id) => apiClient.get(`/listings/${id}`),
  create: (data) => apiClient.post('/listings/', data),
  update: (id, data) => apiClient.put(`/listings/${id}`, data),
  getMy: () => apiClient.get('/listings/seller/my'),
  getCategories: () => apiClient.get('/listings/categories'),
};

// Orders API
export const ordersAPI = {
  create: (data) => apiClient.post('/orders/', data),
  getBuyerOrders: () => apiClient.get('/orders/buyer/my'),
  getSellerOrders: () => apiClient.get('/orders/seller/my'),
  getById: (id) => apiClient.get(`/orders/${id}`),
  updateStatus: (id, status) => apiClient.put(`/orders/${id}/status`, { status }),
};

// Leads API
export const leadsAPI = {
  upload: (data) => apiClient.post('/leads/upload', data),
  getByOrder: (orderId) => apiClient.get(`/leads/order/${orderId}`),
  downloadCSV: (orderId) => apiClient.get(`/leads/order/${orderId}/download`),
};

// Messages API
export const messagesAPI = {
  send: (data) => apiClient.post('/messages/', data),
  getByOrder: (orderId) => apiClient.get(`/messages/order/${orderId}`),
};

// Disputes API
export const disputesAPI = {
  create: (data) => apiClient.post('/disputes/', data),
  getByOrder: (orderId) => apiClient.get(`/disputes/order/${orderId}`),
};

// Admin API
export const adminAPI = {
  getUsers: () => apiClient.get('/admin/users'),
  verifyUser: (userId) => apiClient.put(`/admin/users/${userId}/verify`),
  getPendingListings: () => apiClient.get('/admin/listings/pending'),
  approveListing: (listingId) => apiClient.put(`/admin/listings/${listingId}/approve`),
  rejectListing: (listingId, reason) => apiClient.put(`/admin/listings/${listingId}/reject`, { reason }),
  getAllOrders: () => apiClient.get('/admin/orders'),
  getDisputes: () => apiClient.get('/admin/disputes'),
  resolveDispute: (disputeId, resolution) => apiClient.put(`/admin/disputes/${disputeId}/resolve`, { resolution }),
  getAnalytics: () => apiClient.get('/admin/analytics'),
};

// AALIGN Integration API
export const aalignAPI = {
  getCampaigns: () => apiClient.get('/aalign/campaigns'),
  getCampaignsDetailed: () => apiClient.get('/aalign/campaigns/detailed'),
  getCampaignDetail: (campaignId) => apiClient.get(`/aalign/campaigns/${campaignId}`),
  syncCampaign: (orderId) => apiClient.post(`/aalign/sync-campaign/${orderId}`),
  syncLeads: (orderId) => apiClient.post(`/aalign/sync-leads/${orderId}`),
};

// Marketplace API (AALIGN Auto-Login & Business Operations)
export const marketplaceAPI = {
  autoLogin: (id, role) => apiClient.post('/marketplace/auto-login', { id, role }),
  checkLogin: (id, role) => apiClient.post('/marketplace/auto-login', { id, role }),
  sendRequest: (data) => apiClient.post('/marketplace/send-request', data),
  bookCampaign: (data) => apiClient.post('/marketplace/book-campaign', data),
  getMyBookings: () => apiClient.get('/marketplace/my-bookings'),
  getUserDetails: (userId, role = 'buyer') => apiClient.get(`/marketplace/user/${userId}?role=${role}`),
  getSellers: () => apiClient.get('/marketplace/sellers'),
  getSellerProfile: (sellerId) => apiClient.get(`/marketplace/sellers/${sellerId}`),
  getMyListings: () => apiClient.get('/marketplace/my-listings'),
  getMyListingDetail: (campaignId) => apiClient.get(`/marketplace/my-listings/${campaignId}`),
};

// Utility API
export const utilityAPI = {
  seedData: () => apiClient.post('/seed'),
  healthCheck: () => apiClient.get('/health'),
};

export default apiClient;
